import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Search, Check, Users, ChevronDown } from 'lucide-react'
import { notifyCouple } from '../../lib/notifications'

export default function CheckinPage() {
  const { token } = useParams()
  const [wedding,  setWedding]  = useState(null)
  const [events,   setEvents]   = useState([])
  const [guests,   setGuests]   = useState([])
  const [search,   setSearch]   = useState('')
  const [selEvent, setSelEvent] = useState(null)
  const [loading,  setLoading]  = useState(true)
  const [checking, setChecking] = useState(null)
  const [invalid,  setInvalid]  = useState(false)

  // PIN gate
  const [needsPin,  setNeedsPin]  = useState(false)
  const [pin,       setPin]       = useState('')
  const [pinErr,    setPinErr]    = useState('')
  const [storedPin, setStoredPin] = useState('')
  const pinRef = useRef(null)

  useEffect(() => {
    async function load() {
      if (!token) { setInvalid(true); setLoading(false); return }

      const { data: w } = await supabase.rpc('get_checkin_wedding', { p_token: token })

      if (!w) { setInvalid(true); setLoading(false); return }
      setWedding(w)
      setStoredPin(w.checkin_pin || '')

      // Check if already authenticated this session
      const sessionKey = `checkin_${token}`
      if (w.checkin_pin && sessionStorage.getItem(sessionKey) !== 'ok') {
        setNeedsPin(true)
        setLoading(false)
        setTimeout(() => pinRef.current?.focus(), 100)
        return
      }

      // Load events + guests
      const evs = w.wedding_events || []
      setEvents(evs)
      if (evs.length > 0) setSelEvent(evs[0])

      const { data: g } = await supabase.rpc('get_checkin_guests', { p_token: token })
      setGuests(g || [])
      setLoading(false)
    }
    load()
  }, [token])

  // Light polling as a fallback (the guests table is locked to anon, so RLS
  // realtime isn't available on this public link — broadcast handles live sync).
  useEffect(() => {
    if (!token || invalid || needsPin) return
    const id = setInterval(() => {
      supabase.rpc('get_checkin_guests', { p_token: token })
        .then(({ data }) => { if (data) setGuests(data) })
    }, 15000)
    return () => clearInterval(id)
  }, [token, invalid, needsPin])

  // Real-time arrival sync across attendants + the couple's wedding-day page
  const arrivalsRef = useRef(null)
  useEffect(() => {
    if (!wedding?.id) return
    const ch = supabase.channel(`arrivals-${wedding.id}`)
      .on('broadcast', { event: 'arrival' }, ({ payload }) => {
        setGuests(prev => prev.map(g => g.id === payload.guestId ? { ...g, ...payload.fields } : g))
      })
      .subscribe()
    arrivalsRef.current = ch
    return () => { supabase.removeChannel(ch); arrivalsRef.current = null }
  }, [wedding?.id])

  function submitPin() {
    if (pin === storedPin) {
      sessionStorage.setItem(`checkin_${token}`, 'ok')
      setNeedsPin(false)
      setPinErr('')
      setLoading(true)
      // Load events + guests after PIN accepted
      const evs = wedding.wedding_events || []
      setEvents(evs)
      if (evs.length > 0) setSelEvent(evs[0])
      supabase.rpc('get_checkin_guests', { p_token: token })
        .then(({ data }) => { setGuests(data || []); setLoading(false) })
    } else {
      setPinErr('Incorrect PIN. Check the message from the couple.')
      setPin('')
    }
  }

  async function checkIn(guest) {
    if (checking) return
    setChecking(guest.id)
    const eventId = selEvent?.id
    const currentIds = guest.arrived_event_ids || []
    const alreadyArrived = currentIds.includes(eventId)

    if (alreadyArrived) {
      const newIds = currentIds.filter(id => id !== eventId)
      const { error } = await supabase.rpc('toggle_guest_arrival', { p_token: token, p_guest_id: guest.id, p_event_id: eventId })
      if (!error) {
        const fields = { arrived_event_ids: newIds, arrived: newIds.length > 0 }
        setGuests(prev => prev.map(g => g.id === guest.id ? { ...g, ...fields } : g))
        arrivalsRef.current?.send({ type:'broadcast', event:'arrival', payload:{ guestId: guest.id, fields } })
      }
    } else {
      const newIds = [...currentIds, eventId]
      const { error } = await supabase.rpc('toggle_guest_arrival', { p_token: token, p_guest_id: guest.id, p_event_id: eventId })
      if (!error) {
        const fields = { arrived_event_ids: newIds, arrived: true, arrived_at: new Date().toISOString() }
        setGuests(prev => prev.map(g => g.id === guest.id ? { ...g, ...fields } : g))
        arrivalsRef.current?.send({ type:'broadcast', event:'arrival', payload:{ guestId: guest.id, fields } })
        notifyCouple(wedding.id, '✅ Guest Arrived!', `${guest.name} has checked in`, 'checkin')
      }
    }
    setChecking(null)
  }

  // ── PIN SCREEN ─────────────────────────────────────────────
  if (needsPin) return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem' }}>
      <div style={{ width:'100%', maxWidth:340, textAlign:'center' }}>
        <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:40, objectFit:'contain', display:'block', margin:'0 auto 1.25rem' }}/>
        <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.4rem', color:'#1A1A1A', margin:'0 0 0.35rem' }}>
          {wedding?.partner1_name} &amp; {wedding?.partner2_name}
        </h1>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#9B9B9B', margin:'0 0 1.5rem' }}>
          Enter the 4-digit PIN to access check-in
        </p>
        <input
          ref={pinRef}
          type="text"
          inputMode="numeric"
          maxLength={4}
          value={pin}
          onChange={e => { setPin(e.target.value.replace(/\D/g,'')); setPinErr('') }}
          onKeyDown={e => { if (e.key === 'Enter' && pin.length === 4) submitPin() }}
          placeholder="• • • •"
          style={{ width:'100%', maxWidth:180, fontFamily:'Nunito Sans, sans-serif', fontSize:'1.5rem', fontWeight:700, textAlign:'center', letterSpacing:'0.5em', padding:'0.75rem 1rem', border:'1.5px solid #E5E0D8', borderRadius:'0.875rem', outline:'none', boxSizing:'border-box', margin:'0 auto', display:'block' }}
        />
        {pinErr && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#DC2626', margin:'0.75rem 0 0' }}>{pinErr}</p>}
        <button
          onClick={submitPin}
          disabled={pin.length !== 4}
          style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.875rem', color:'white', background: pin.length === 4 ? 'linear-gradient(135deg,#1A1A1A,#2A1810)' : '#D4C4BC', border:'none', borderRadius:100, padding:'0.75rem 2rem', cursor: pin.length === 4 ? 'pointer' : 'default', marginTop:'1rem', transition:'background 0.15s' }}>
          Verify
        </button>
      </div>
    </div>
  )

  // ── LOADING / INVALID ──────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>Loading check-in…</p>
    </div>
  )

  if (invalid || !wedding) return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'2rem' }}>
      <div>
        <p style={{ fontFamily:'Gibs, serif', fontSize:'1.4rem', color:'#1A1A1A', marginBottom:'0.5rem' }}>Invalid check-in link</p>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC', fontSize:'0.875rem', maxWidth:320, margin:'0 auto' }}>
          This link is no longer valid. Ask the couple for a new check-in link.
        </p>
      </div>
    </div>
  )

  // ── MAIN CHECK-IN UI ───────────────────────────────────────
  const eventGuests = selEvent
    ? guests.filter(g => (g.invited_event_ids || []).includes(selEvent.id) || (g.invited_event_ids || []).length === 0)
    : guests
  const filtered = eventGuests.filter(g => g.name?.toLowerCase().includes(search.toLowerCase()))
  const arrivedCount = selEvent
    ? guests.filter(g => (g.arrived_event_ids || []).includes(selEvent.id)).length
    : guests.filter(g => g.arrived).length
  const totalCount = eventGuests.length
  const pct = totalCount > 0 ? Math.round((arrivedCount / totalCount) * 100) : 0

  return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', fontFamily:'Nunito Sans, sans-serif' }}>
      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#1A1A1A,#2A1810)', padding:'1.25rem 1.5rem', position:'sticky', top:0, zIndex:20 }}>
        <div style={{ maxWidth:520, margin:'0 auto' }}>
          <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:24, objectFit:'contain', filter:'brightness(10)', display:'block', marginBottom:'0.5rem' }}/>
          <p style={{ fontSize:'0.65rem', color:'rgba(245,239,230,0.5)', letterSpacing:'0.15em', textTransform:'uppercase', margin:'0 0 0.2rem' }}>Check-in</p>
          <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.3rem', color:'rgba(245,239,230,0.95)', margin:'0 0 0.75rem', lineHeight:1.2 }}>
            {wedding.partner1_name} &amp; {wedding.partner2_name}
          </h1>
          {events.length > 1 && (
            <div style={{ position:'relative', marginBottom:'0.75rem' }}>
              <select value={selEvent?.id || ''} onChange={e => setSelEvent(events.find(ev => ev.id === e.target.value))}
                style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', padding:'0.6rem 2rem 0.6rem 0.875rem', background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'0.75rem', color:'rgba(245,239,230,0.9)', outline:'none', cursor:'pointer', appearance:'none' }}>
                {events.map(ev => <option key={ev.id} value={ev.id} style={{ background:'#1A1A1A' }}>{ev.type}{ev.date ? ` — ${new Date(ev.date).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}` : ''}</option>)}
              </select>
              <ChevronDown size={14} color="rgba(245,239,230,0.5)" style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
            </div>
          )}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.4rem' }}>
            <span style={{ fontSize:'0.72rem', color:'rgba(245,239,230,0.6)' }}>{arrivedCount} of {totalCount} arrived</span>
            <span style={{ fontSize:'0.72rem', color:'rgba(155,175,155,0.9)', fontWeight:700 }}>{pct}%</span>
          </div>
          <div style={{ height:4, background:'rgba(255,255,255,0.1)', borderRadius:100, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${pct}%`, background:'linear-gradient(to right,#9BAF9B,#7A917A)', borderRadius:100, transition:'width 0.4s' }}/>
          </div>
        </div>
      </div>

      {/* Tip: what guests will present */}
      <div style={{ maxWidth:520, margin:'0 auto', padding:'1rem 1rem 0' }}>
        <div style={{ background:'rgba(196,130,106,0.07)', border:'1px solid rgba(196,130,106,0.18)', borderRadius:'0.75rem', padding:'0.7rem 0.875rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', color:'#8C6B57', margin:0, lineHeight:1.55 }}>
            💡 Guests may show a saved <strong>RSVP confirmation card</strong> on their phone. Match the name on it, search below, and tap to check them in.
          </p>
        </div>
      </div>

      {/* Search */}
      <div style={{ maxWidth:520, margin:'0 auto', padding:'1rem 1rem 0' }}>
        <div style={{ position:'relative' }}>
          <Search size={16} color="#BCBCBC" style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
          <input placeholder="Search guest name…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', padding:'0.7rem 0.875rem 0.7rem 2.25rem', background:'white', border:'1.5px solid #E5E0D8', borderRadius:'0.875rem', outline:'none', boxSizing:'border-box' }}/>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.75rem 0 0.25rem' }}>
          <Users size={13} color="#BCBCBC"/>
          <span style={{ fontSize:'0.72rem', color:'#BCBCBC' }}>{filtered.length} guest{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Guest list */}
      <div style={{ maxWidth:520, margin:'0 auto', padding:'0.5rem 1rem 2rem' }}>
        {filtered.length === 0 && (
          <p style={{ textAlign:'center', color:'#BCBCBC', padding:'2rem', fontSize:'0.875rem' }}>
            {search ? 'No guest matches that name.' : 'No guests added yet.'}
          </p>
        )}
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {filtered.map(guest => {
            const arrived = selEvent ? (guest.arrived_event_ids || []).includes(selEvent.id) : guest.arrived
            return (
              <button key={guest.id} onClick={() => checkIn(guest)} disabled={!!checking}
                style={{ display:'flex', alignItems:'center', gap:'0.875rem', background: arrived ? 'rgba(155,175,155,0.04)' : 'white',
                  border:`1.5px solid ${arrived ? 'rgba(155,175,155,0.5)' : '#E5E0D8'}`,
                  borderRadius:'0.875rem', padding:'0.875rem 1rem', cursor:'pointer', textAlign:'left',
                  transition:'all 0.15s', opacity:checking === guest.id ? 0.6 : 1 }}>
                <div style={{ width:38, height:38, borderRadius:'50%', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
                  background: arrived ? 'rgba(155,175,155,0.15)' : '#F3F0EA',
                  border: arrived ? '1.5px solid rgba(155,175,155,0.4)' : '1.5px solid transparent' }}>
                  {arrived ? <Check size={16} color="#7A917A"/> : <span style={{ fontFamily:'Gibs, serif', fontSize:'1rem', color:'#BCBCBC' }}>{(guest.name||'?')[0].toUpperCase()}</span>}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontWeight:700, fontSize:'0.875rem', color: arrived ? '#7A917A' : '#1A1A1A', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{guest.name}</p>
                  <p style={{ fontSize:'0.7rem', color:'#BCBCBC', margin:'1px 0 0' }}>
                    {guest.plus_one ? 'Party of 2' : 'Guest'}
                    {arrived && guest.arrived_at ? ` · Arrived ${new Date(guest.arrived_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}` : ''}
                  </p>
                </div>
                <span style={{ fontSize:'0.65rem', fontWeight:700, flexShrink:0,
                  color: arrived ? '#7A917A' : '#BCBCBC',
                  background: arrived ? 'rgba(155,175,155,0.12)' : '#F3F0EA',
                  borderRadius:100, padding:'3px 10px' }}>
                  {arrived ? 'Arrived' : 'Not yet'}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Okoiya branding */}
      <div style={{ textAlign:'center', padding:'1.5rem 1rem 2rem' }}>
        <a href="https://okoiya.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', display:'inline-flex', alignItems:'center', gap:'0.4rem', background:'#F3F0EA', borderRadius:100, padding:'0.4rem 1rem', border:'1px solid #E5E0D8', marginBottom:'0.625rem' }}>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'rgba(3,1,0,0.5)' }}>Made with</span>
          <span style={{ fontFamily:'Gibs, serif', fontSize:'0.8rem', color:'#C4826A' }}>Okoiya</span>
        </a>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#BCBCBC', margin:'0.4rem 0 0' }}>
          Planning your own wedding?{' '}
          <a href="/signup" style={{ color:'#C4826A', fontWeight:700, textDecoration:'none' }}>
            Become an Okoiya Couple today →
          </a>
        </p>
      </div>
    </div>
  )
}
